// Cross-component channel for a small paginator that renders in AppHeader,
// independently of the variable-width breadcrumb navigation. The view
// that owns the paginated state writes its state here on mount/route change
// and clears it on unmount. AppHeader reads the state and renders
// a discrete button group — visually distinct from the breadcrumb itself
// so the chevrons read as a control, not as another crumb.
import { defineStore } from 'pinia'

interface PagerState {
  active: boolean
  positionLabel: string
  hasPrev: boolean
  hasNext: boolean
  disabledReason: string | null
  onPrev: (() => void) | null
  onNext: (() => void) | null
  ariaLabel: string
}

const empty = (): PagerState => ({
  active: false,
  positionLabel: '',
  hasPrev: false,
  hasNext: false,
  disabledReason: null,
  onPrev: null,
  onNext: null,
  ariaLabel: 'Pagination',
})

export const useBreadcrumbPagerStore = defineStore('breadcrumbPager', {
  state: (): PagerState => empty(),
  actions: {
    set(state: Partial<PagerState>): void {
      Object.assign(this, { ...empty(), active: true, ...state })
    },
    clear(): void {
      Object.assign(this, empty())
    },
    prev(): void {
      if (this.hasPrev && !this.disabledReason) this.onPrev?.()
    },
    next(): void {
      if (this.hasNext && !this.disabledReason) this.onNext?.()
    },
  },
})
